import React, { createContext, useContext, useReducer, useMemo } from 'react';
import { PARTICLES, MATERIALS, C } from '../data/physicsConstants';
import { 
    calculateKinematics, 
    calculateDecayLength, 
    calculateLabLifetime, 
    calculateMCSAngle, 
    calculateMCSVariance 
} from '../utils/physicsUtils';

// ============================================
// PHYSICS CALCULATIONS (Pure Functions)
// ============================================

export const calculateVarianceContrast = (material1, material2, momentum, mass, thickness) => {
    // Ratio of scattering variances for two materials
    const var1 = calculateMCSVariance(momentum, mass, thickness, material1.X0);
    const var2 = calculateMCSVariance(momentum, mass, thickness, material2.X0);
    return var1 / var2;
};

// ============================================
// EXPERIMENT TYPE HELPER
// ============================================
export const getExperimentType = (state) => {
    const { activePreset, physics, material, phantomThickness, particle } = state;

    // Check preset category if available
    if (activePreset) {
        const preset = activePreset.toLowerCase();
        if (preset.includes('cosmic') || preset.includes('rossi')) return 'cosmic';
        if (preset.includes('cloud')) return 'cloud-chamber';
        if (preset.includes('medical') || preset.includes('therapy') || preset.includes('proton-ct') || preset.includes('bragg')) return 'medical';
        if (preset.includes('decay') || preset.includes('lifetime') || preset.includes('pion-decay') || preset.includes('muon-decay') || preset.includes('kaon-decay')) return 'decay';
        if (preset.includes('tof') || preset.includes('pid') || preset.includes('cherenkov')) return 'particle-id';
        if (preset.includes('calorimeter') || preset.includes('shower') || preset.includes('em-shower') || preset.includes('hadronic')) return 'calorimeter';
        if (preset.includes('rutherford')) return 'rutherford';
        if (preset.includes('relativity') || preset.includes('time-dilation') || preset.includes('gamma')) return 'relativity';
        if (preset.includes('tracker') || preset.includes('silicon') || preset.includes('alignment')) return 'tracker';
        if (preset.includes('magnetic') || preset.includes('bending') || preset.includes('spectrometer')) return 'magnetic';
        if (preset.includes('transition') || preset.includes('trd')) return 'trd';
    }

    // Fallback detection based on physics state
    if (physics?.mcsEnabled && phantomThickness > 0) return 'mcs-tomography';
    if (physics?.decayEnabled && particle?.lifetime < Infinity) return 'decay';
    if (material === 'air' && phantomThickness === 0) return 'particle-id';

    return 'mcs-tomography'; // default
};

// ============================================
// INITIAL STATE
// ============================================
const createInitialState = () => {
    const particle = PARTICLES.proton;
    const material = MATERIALS.pvc;
    const momentum = 0.7; // GeV/c (Z-SCANNER default)
    const thickness = 2.0; // cm (phantom thickness)
    const { energy, beta, gamma } = calculateKinematics(momentum, particle.mass);
    const decayLength = calculateDecayLength(beta, gamma, particle.lifetime);
    const labLifetime = calculateLabLifetime(gamma, particle.lifetime);
    const thetaRMS = calculateMCSAngle(momentum, particle.mass, thickness, material.X0);

    return {
        // Particle selection
        particle,

        // Material selection (for MCS)
        material: 'pvc',
        insertMaterial: null, // Optional insert with different material
        phantomThickness: thickness, // cm
        phantomSize: 3, // cm (3x3 cm phantom)

        // Beam parameters
        beam: {
            momentum,
            intensity: 10000,
        },

        // Beamline geometry
        beamline: {
            length: 0.2, // meters (20 cm for MCS setup)
        },

        // Active detectors/trackers
        detectors: {
            upstreamTracker1: true,
            upstreamTracker2: true,
            phantom: true,
            downstreamTracker1: true,
            downstreamTracker2: true,
        },

        // Physics settings
        physics: {
            decayEnabled: false, // Protons don't decay
            mcsEnabled: true,    // MCS physics enabled
            magneticField: 0,    // Tesla
            showSecondaries: false,
        },

        // Analysis settings
        analysis: {
            mode: 'mcs', // 'mcs', 'decay', 'single', 'continuous'
            targetEvents: 10000,
            showErrorBars: true,
            fitCurve: true,
            normalize: true,
        },

        // RNG seed for reproducibility
        rngSeed: Math.floor(Date.now() / 1000),

        // Detector response (toy model)
        detectorResponse: {
            enabled: true,
            efficiency: 0.92,
            angularResolution: 0.1, // mrad
            positionResolution: 100e-6, // 100 μm
        },

        // Derived physics values (auto-calculated)
        derived: {
            energy,
            beta,
            gamma,
            decayLength,
            labLifetime,
            expectedSurvival: particle.lifetime === Infinity ? 1 : Math.exp(-0.2 / decayLength),
            mcsAngle: thetaRMS * 1000, // mrad
            thetaRMS,
            thetaRMSmrad: thetaRMS * 1000,
            variance: thetaRMS * thetaRMS,
        },

        // Simulation state
        simulation: {
            running: false,
            progress: 0,
            results: null,
            error: null,
        },

        // Active preset (for UI highlighting)
        activePreset: 'z-scanner-pvc',

        // UI State
        controlRoomOpen: false,
    };
};

// ============================================
// ACTION TYPES
// ============================================
const ActionTypes = {
    SET_PARTICLE: 'SET_PARTICLE',
    SET_MATERIAL: 'SET_MATERIAL',
    SET_INSERT_MATERIAL: 'SET_INSERT_MATERIAL',
    SET_PHANTOM_THICKNESS: 'SET_PHANTOM_THICKNESS',
    SET_PHANTOM_SIZE: 'SET_PHANTOM_SIZE',
    SET_MOMENTUM: 'SET_MOMENTUM',
    SET_BEAM_INTENSITY: 'SET_BEAM_INTENSITY',
    SET_BEAMLINE_LENGTH: 'SET_BEAMLINE_LENGTH',
    TOGGLE_DETECTOR: 'TOGGLE_DETECTOR',
    SET_PHYSICS_DECAY: 'SET_PHYSICS_DECAY',
    SET_PHYSICS_MCS: 'SET_PHYSICS_MCS',
    SET_MAGNETIC_FIELD: 'SET_MAGNETIC_FIELD',
    SET_ANALYSIS_MODE: 'SET_ANALYSIS_MODE',
    SET_CONTROL_ROOM_OPEN: 'SET_CONTROL_ROOM_OPEN',
    SET_TARGET_EVENTS: 'SET_TARGET_EVENTS',
    TOGGLE_ANALYSIS_OPTION: 'TOGGLE_ANALYSIS_OPTION',
    SET_RNG_SEED: 'SET_RNG_SEED',
    SET_DETECTOR_RESPONSE: 'SET_DETECTOR_RESPONSE',
    LOAD_PRESET: 'LOAD_PRESET',
    START_SIMULATION: 'START_SIMULATION',
    UPDATE_PROGRESS: 'UPDATE_PROGRESS',
    COMPLETE_SIMULATION: 'COMPLETE_SIMULATION',
    SIMULATION_ERROR: 'SIMULATION_ERROR',
    RESET_SIMULATION: 'RESET_SIMULATION',
};

// ============================================
// REDUCER
// ============================================
const recalculateDerived = (state) => {
    const { beta, gamma, energy } = calculateKinematics(
        state.beam.momentum,
        state.particle.mass
    );
    // Decay Physics
    // If decay is disabled in physics settings, treat as stable (Infinity lifetime)
    const effectiveLifetime = state.physics.decayEnabled ? state.particle.lifetime : Infinity;
    const decayLength = calculateDecayLength(beta, gamma, effectiveLifetime);
    const labLifetime = calculateLabLifetime(gamma, effectiveLifetime);
    const expectedSurvival = effectiveLifetime === Infinity
        ? 1
        : Math.exp(-state.beamline.length / decayLength);

    // MCS calculations - use material key to get X0
    const mat = MATERIALS[state.material] || MATERIALS.pvc;
    // If MCS is disabled, thetaRMS is practically 0 (or very small due to air/instrumentation, 
    // but for "toggle" clarity we set to 0)
    const thetaRMS = state.physics.mcsEnabled
        ? calculateMCSAngle(state.beam.momentum, state.particle.mass, state.phantomThickness, mat.X0)
        : 0;

    return {
        ...state,
        derived: {
            energy,
            beta,
            gamma,
            decayLength,
            labLifetime,
            expectedSurvival,
            mcsAngle: thetaRMS * 1000, // mrad
            thetaRMS,
            thetaRMSmrad: thetaRMS * 1000,
            variance: thetaRMS * thetaRMS,
        }
    };
};

const simulationReducer = (state, action) => {
    let newState;

    switch (action.type) {
        case ActionTypes.SET_PARTICLE:
            newState = {
                ...state,
                particle: PARTICLES[action.payload],
                // activePreset: null, // Removed to persist preset context
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_MATERIAL:
            newState = {
                ...state,
                material: action.payload, // Store as string key
                // activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_INSERT_MATERIAL:
            newState = {
                ...state,
                insertMaterial: action.payload, // Store as string key or null
                // activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_PHANTOM_THICKNESS:
            newState = {
                ...state,
                phantomThickness: action.payload,
                // activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_PHANTOM_SIZE:
            newState = {
                ...state,
                phantomSize: action.payload,
                // activePreset: null,
            };
            return newState;

        case ActionTypes.SET_MOMENTUM:
            newState = {
                ...state,
                beam: { ...state.beam, momentum: action.payload },
                // activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.SET_BEAM_INTENSITY:
            return {
                ...state,
                beam: { ...state.beam, intensity: action.payload },
            };

        case ActionTypes.SET_BEAMLINE_LENGTH:
            newState = {
                ...state,
                beamline: { ...state.beamline, length: action.payload },
                // activePreset: null,
            };
            return recalculateDerived(newState);

        case ActionTypes.TOGGLE_DETECTOR:
            return {
                ...state,
                detectors: {
                    ...state.detectors,
                    [action.payload]: !state.detectors[action.payload],
                },
                // activePreset: null,
            };

        case ActionTypes.SET_PHYSICS_DECAY:
            return {
                ...state,
                physics: { ...state.physics, decayEnabled: action.payload },
            };

        case ActionTypes.SET_MAGNETIC_FIELD:
            return {
                ...state,
                physics: { ...state.physics, magneticField: action.payload },
                // activePreset: null,
            };

        case ActionTypes.SET_ANALYSIS_MODE:
            return {
                ...state,
                analysis: { ...state.analysis, mode: action.payload },
            };

        case ActionTypes.SET_TARGET_EVENTS:
            return {
                ...state,
                analysis: { ...state.analysis, targetEvents: action.payload },
            };

        case ActionTypes.TOGGLE_ANALYSIS_OPTION:
            return {
                ...state,
                analysis: {
                    ...state.analysis,
                    [action.payload]: !state.analysis[action.payload],
                },
            };

        case ActionTypes.SET_RNG_SEED:
            return {
                ...state,
                rngSeed: action.payload,
            };

        case ActionTypes.SET_DETECTOR_RESPONSE:
            return {
                ...state,
                detectorResponse: action.payload,
            };

        case ActionTypes.SET_CONTROL_ROOM_OPEN:
            return {
                ...state,
                controlRoomOpen: action.payload,
            };

        case ActionTypes.LOAD_PRESET:
            const preset = action.payload;
            newState = {
                ...state,
                particle: PARTICLES[preset.particle] || PARTICLES.proton,
                material: preset.material || 'pvc',
                insertMaterial: preset.insertMaterial || null,
                phantomThickness: preset.phantomThickness ?? 2.0,
                beam: {
                    momentum: preset.momentum ?? 0.7,
                    intensity: preset.beamIntensity ?? 10000,
                },
                beamline: {
                    length: preset.beamlineLength ?? 0.2,
                },
                detectors: preset.detectors ? { ...preset.detectors } : state.detectors,
                physics: {
                    decayEnabled: preset.physics?.decayEnabled ?? state.physics.decayEnabled,
                    magneticField: state.physics.magneticField, // Keep user preference
                    showSecondaries: false,
                    mcsEnabled: preset.physics?.mcsEnabled ?? state.physics.mcsEnabled,
                },
                activePreset: preset.id,
            };
            return recalculateDerived(newState);

        case ActionTypes.START_SIMULATION:
            return {
                ...state,
                simulation: {
                    running: true,
                    progress: 0,
                    results: null,
                    error: null,
                },
            };

        case ActionTypes.UPDATE_PROGRESS:
            return {
                ...state,
                simulation: {
                    ...state.simulation,
                    progress: action.payload,
                },
            };

        case ActionTypes.COMPLETE_SIMULATION:
            return {
                ...state,
                simulation: {
                    running: false,
                    progress: 100,
                    results: action.payload,
                    error: null,
                },
            };

        case ActionTypes.SIMULATION_ERROR:
            return {
                ...state,
                simulation: {
                    running: false,
                    progress: 0,
                    results: null,
                    error: action.payload,
                },
            };

        case ActionTypes.RESET_SIMULATION:
            return {
                ...state,
                simulation: {
                    running: false,
                    progress: 0,
                    results: null,
                    error: null,
                },
            };

        default:
            return state;
    }
};

// ============================================
// CONTEXT
// ============================================
const SimulationContext = createContext(null);

export const SimulationProvider = ({ children }) => {
    const [state, dispatch] = useReducer(simulationReducer, null, createInitialState);

    // Memoized action creators
    const actions = useMemo(() => ({
        setParticle: (type) => dispatch({ type: ActionTypes.SET_PARTICLE, payload: type }),
        setMaterial: (type) => dispatch({ type: ActionTypes.SET_MATERIAL, payload: type }),
        setInsertMaterial: (type) => dispatch({ type: ActionTypes.SET_INSERT_MATERIAL, payload: type }),
        setPhantomThickness: (t) => dispatch({ type: ActionTypes.SET_PHANTOM_THICKNESS, payload: t }),
        setPhantomSize: (s) => dispatch({ type: ActionTypes.SET_PHANTOM_SIZE, payload: s }),
        setMomentum: (p) => dispatch({ type: ActionTypes.SET_MOMENTUM, payload: p }),
        setBeamIntensity: (n) => dispatch({ type: ActionTypes.SET_BEAM_INTENSITY, payload: n }),
        setBeamlineLength: (l) => dispatch({ type: ActionTypes.SET_BEAMLINE_LENGTH, payload: l }),
        toggleDetector: (name) => dispatch({ type: ActionTypes.TOGGLE_DETECTOR, payload: name }),
        setDecayEnabled: (enabled) => dispatch({ type: ActionTypes.SET_PHYSICS_DECAY, payload: enabled }),
        setMCSEnabled: (enabled) => dispatch({ type: ActionTypes.SET_PHYSICS_MCS, payload: enabled }),
        setMagneticField: (B) => dispatch({ type: ActionTypes.SET_MAGNETIC_FIELD, payload: B }),
        setAnalysisMode: (mode) => dispatch({ type: ActionTypes.SET_ANALYSIS_MODE, payload: mode }),
        setTargetEvents: (n) => dispatch({ type: ActionTypes.SET_TARGET_EVENTS, payload: n }),
        toggleAnalysisOption: (opt) => dispatch({ type: ActionTypes.TOGGLE_ANALYSIS_OPTION, payload: opt }),
        setRngSeed: (seed) => dispatch({ type: ActionTypes.SET_RNG_SEED, payload: seed }),
        setDetectorResponse: (config) => dispatch({ type: ActionTypes.SET_DETECTOR_RESPONSE, payload: config }),
        setControlRoomOpen: (isOpen) => dispatch({ type: ActionTypes.SET_CONTROL_ROOM_OPEN, payload: isOpen }),
        loadPreset: (preset) => dispatch({ type: ActionTypes.LOAD_PRESET, payload: preset }),
        startSimulation: () => dispatch({ type: ActionTypes.START_SIMULATION }),
        updateProgress: (p) => dispatch({ type: ActionTypes.UPDATE_PROGRESS, payload: p }),
        completeSimulation: (results) => dispatch({ type: ActionTypes.COMPLETE_SIMULATION, payload: results }),
        simulationError: (err) => dispatch({ type: ActionTypes.SIMULATION_ERROR, payload: err }),
        resetSimulation: () => dispatch({ type: ActionTypes.RESET_SIMULATION }),
    }), []);

    const value = useMemo(() => ({ state, actions, dispatch }), [state, actions]);

    return (
        <SimulationContext.Provider value={value}>
            {children}
        </SimulationContext.Provider>
    );
};

// ============================================
// HOOK
// ============================================
export const useSimulationContext = () => {
    const context = useContext(SimulationContext);
    if (!context) {
        throw new Error('useSimulationContext must be used within a SimulationProvider');
    }
    return context;
};

export default SimulationContext;
